import React from 'react';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { AllApplicationsChart } from 'elements/Charts/AllApplicationsChart';
import { AppTypeChart } from 'elements/Charts/AppTypeChart';
import { AppPerformanceReview } from 'elements/Charts/AppPerformanceReview';
import { AppDistribution } from 'elements/Charts/AppDistribution';
import { TotalClaimChart } from 'elements/Charts/TotalClaimChart';
import { ClaimPaymentsChart } from 'elements/Charts/ClaimPaymentsChart';
import { ClaimFileStatusChart } from 'elements/Charts/ClaimFileStatusChart';
import { GrossRentChart } from 'elements/Charts/GrossRentChart';
import { ApplicationVolume6MonthsChart } from 'elements/Charts/ApplicationVolume6MonthsChart';
import { ApplicationStageChart } from 'elements/Charts/ApplicationStageChart';
import { DeclinesByReasonChart } from 'elements/Charts/DeclinesByReasonChart';
import { CancellationsByCancellationReasonChart } from 'elements/Charts/CancellationsByCancellationReasonChart';
import { ExpiringPoliciesChart } from 'elements/Charts/ExpiringPoliciesChart';
import { CoverageChart } from 'elements/Charts/CoverageChart';
import { useNavigate } from 'react-router-dom';
import { navigateApplicationsFromDashboardFilter } from 'utils/navigateApplicationsFromDashboardFilter';
import { navigateClaimsFromDashboardFilter } from 'utils/navigateClaimsFromDashboardFilter';
import { navigatePoliciesFromDashboardFilter } from 'utils/policyDashboardNavigation';
import { PolicyIssuedChart } from 'elements/Charts/PolicyIssuedChart';

export const AdminReport = ({ data }) => {
  const navigate = useNavigate();

  const navigateApplicationsFiltered = (dashboardFilter) => {
    navigateApplicationsFromDashboardFilter(navigate, dashboardFilter);
  };

  const navigateClaimsFiltered = (claimsDashboardFilter) => {
    navigateClaimsFromDashboardFilter(navigate, claimsDashboardFilter);
  };

  const navigatePoliciesFiltered = (dashboardFilter) => {
    navigatePoliciesFromDashboardFilter(navigate, dashboardFilter);
  };

  const navigateApplicationsOverview = () => navigate('/property/applications');
  const navigatePoliciesOverview = () => navigate('/property/policies');

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Type1ChartContainer>
        <PirChartContainer>
          <AllApplicationsChart
            data={data}
            onClickItem={(stage) => navigateApplicationsFiltered(stage)}
          />
        </PirChartContainer>
        <PirChartContainer>
          <AppTypeChart data={data} onClickItem={(type) => navigateApplicationsFiltered(type)} />
        </PirChartContainer>
      </Type1ChartContainer>

      <PolicyIssuedChart data={data} />

      <AppPerformanceReview
        data={data}
        onApplicationsDrilldown={(key) => navigateApplicationsFiltered(key)}
      />
      <AppDistribution
        data={data}
        onApplicationsDrilldown={(key) => navigateApplicationsFiltered(key)}
      />
      {data.claimCountsByMonth && (
        <React.Fragment>
          <TotalClaimChart
            data={data}
            onDrilldownOpen={() => navigateClaimsFiltered('claims_summary_open')}
            onDrilldownClosed={() => navigateClaimsFiltered('claims_summary_closed')}
          />
          <ClaimPaymentsChart
            data={data}
            onDrilldownClosedBucket={() => navigateClaimsFiltered('claims_payment_closed_bucket')}
            onDrilldownPendingPayment={() => navigateClaimsFiltered('open_pending_payment')}
            onDrilldownOther={() => navigateClaimsFiltered('claims_payment_other')}
          />
          <ClaimFileStatusChart data={data} onClickItem={(key) => navigateClaimsFiltered(key)} />
        </React.Fragment>
      )}

      <Type2ChartContainer>
        <Box flex="1 1 0%">
          <ExpiringPoliciesChart
            data={data}
            onClickItem={(rangeKey) => navigatePoliciesFiltered(rangeKey)}
          />
        </Box>
        <Box flex="1 1 0%">
          <CoverageChart
            data={data}
            onClickItem={(coverageFilter) => navigatePoliciesFiltered(coverageFilter)}
          />
        </Box>
      </Type2ChartContainer>
      <TypeGrossRentChartContainer>
        <GrossRentChart
          data={data}
          onNavigateApplications={navigateApplicationsOverview}
          onNavigatePolicies={navigatePoliciesOverview}
        />
      </TypeGrossRentChartContainer>
      <TypeApplicationVolume6MonthsChartContainer>
        <ApplicationVolume6MonthsChart
          data={data}
          onNavigateApplications={navigateApplicationsOverview}
          onNavigatePolicies={navigatePoliciesOverview}
        />
      </TypeApplicationVolume6MonthsChartContainer>
      <TypeApplicationStageChartContainer>
        <ApplicationStageChart
          data={data}
          onApplicationsDrilldown={(key) => navigateApplicationsFiltered(key)}
        />
      </TypeApplicationStageChartContainer>
      <TypeDeclinesByReasonChart>
        <DeclinesByReasonChart
          data={data}
          onDrilldown={() => navigateApplicationsFiltered('06 - Policy Declined')}
        />
      </TypeDeclinesByReasonChart>
      <TypeCancellationsByCancellationReasonChartContainer>
        <CancellationsByCancellationReasonChart
          data={data}
          onDrilldown={() => navigateApplicationsFiltered('Cancelled Application')}
        />
      </TypeCancellationsByCancellationReasonChartContainer>
    </Box>
  );
};

// Viewport ≤960px was too late — expanded sidebar (240px) leaves
// ~950px content at 1280px, which is narrower than the two cards side-by-side.
// Wrap when either card cannot keep ~520px so we stack before clipping/scroll.
const TYPE1_CHART_CARD_MIN_BASIS = '520px';

const Type1ChartContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  min-width: 0;

  @media screen and (max-width: ${({ theme }) => theme['lg']}) {
    flex-direction: column;
  }
`;

export const PirChartContainer = styled.div`
  flex: 1 1 ${TYPE1_CHART_CARD_MIN_BASIS};
  min-width: 0;
  max-width: 100%;
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['md']}) {
        padding: 16px 6px !important;
        & > div {
          flex-direction: column !important;
          & > div:nth-of-type(2) {
            width: 100%;
            * {
              box-sizing: border-box;
            }
          }
        }
      }
    }

    & > div:nth-of-type(3) {
      @media screen and (max-width: ${({ theme }) => theme['md']}) {
        flex-direction: column;
      }
    }
  }
`;

export const Type2ChartContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;

  @media screen and (max-width: ${({ theme }) => theme['xl']}) {
    flex-direction: column;
  }

  & > div:nth-of-type(1) {
    & > div {
      & > div:nth-of-type(3) {
        @media screen and (max-width: ${({ theme }) => theme['md']}) {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          grid-template-columns: repeat(1, 1fr);
        }
      }
    }
  }

  & > div:nth-of-type(2) {
    & > div {
      & > div:nth-of-type(2) {
        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          padding: 16px 6px !important;
        }
        & > div {
          @media screen and (max-width: ${({ theme }) => theme['sm']}) {
            flex-direction: column;
            * {
              box-sizing: border-box;
            }
            & > div:nth-of-type(2) {
              width: 100%;
            }
          }
        }
      }
      & > div:nth-of-type(3) {
        @media screen and (max-width: ${({ theme }) => theme['md']}) {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          grid-template-columns: repeat(1, 1fr);
        }
      }
    }
  }
`;

export const TypeGrossRentChartContainer = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['lg']}) {
        flex-direction: column;
      }
    }
  }
`;

export const TypeApplicationVolume6MonthsChartContainer = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['lg']}) {
        flex-direction: column;
      }
    }
  }
`;

export const TypeApplicationStageChartContainer = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['lg']}) {
        flex-direction: column;
      }
      & > div:nth-of-type(2) {
        & > div {
          @media screen and (max-width: ${({ theme }) => theme['md']}) {
            flex-direction: column;
          }
        }
      }
    }
  }
`;

export const TypeDeclinesByReasonChart = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['lg']}) {
        flex-direction: column;
      }
      & > div:nth-of-type(2) {
        & > div {
          @media screen and (max-width: ${({ theme }) => theme['md']}) {
            flex-direction: column;
          }
        }
      }
    }
  }
`;

export const TypeCancellationsByCancellationReasonChartContainer = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['lg']}) {
        flex-direction: column;
      }
      & > div:nth-of-type(2) {
        & > div {
          @media screen and (max-width: ${({ theme }) => theme['md']}) {
            flex-direction: column;
          }
        }
      }
    }
  }
`;
